import React, { useState, useEffect, useCallback } from 'react';
import './ProfileUpdateDialog.css';
import { getETNow, getETDateString } from './utils/marketUtils';
import {
  getProfile as apiGetProfile,
  listReviews as apiListReviews,
  getReview as apiGetReview,
  deleteReview as apiDeleteReview,
  mergeReviewsIntoProfile as apiMergeReviewsIntoProfile,
  getEnrichedTrades as apiGetEnriched,
  getSimulatedResultsRange as apiGetSimRange,
} from './services/apiService';
import ReviewDetailDialog from './ReviewDetailDialog';

// ==========================================
// Pure utility functions (no API dependency)
// ==========================================

const timeRangeLabel = (tr) => {
  const labels = { day: 'Daily', week: 'Weekly', month: 'Monthly', all: 'All', custom: 'Custom' };
  return labels[tr] || tr;
};

const formatTimestamp = (ts) => {
  if (!ts) return '';
  const d = new Date(ts);
  if (isNaN(d)) return '';
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd} ${hh}:${min}`;
};

const formatCreatedAt = (createdAt, updatedAt) => {
  if (!createdAt) return '';
  const created = new Date(createdAt);
  const updated = updatedAt ? new Date(updatedAt) : null;
  if (updated && !isNaN(updated) && updated > created) {
    return `${formatTimestamp(updatedAt)} (updated)`;
  }
  return formatTimestamp(createdAt);
};

/**
 * Calculate non-overlapping date ranges for a review relative to existing coverage
 */
const getNonOverlappingRanges = (reviewDateRange, existingRanges) => {
  if (!reviewDateRange || !existingRanges || existingRanges.length === 0) {
    return [{ start: reviewDateRange.start, end: reviewDateRange.end }];
  }

  const addDays = (dateStr, days) => {
    const d = new Date(dateStr);
    d.setDate(d.getDate() + days);
    return d.toISOString().split('T')[0];
  };

  const daysBetween = (a, b) => {
    return Math.round((new Date(b) - new Date(a)) / 86400000);
  };

  let remaining = [{ start: reviewDateRange.start, end: reviewDateRange.end }];

  for (const existing of existingRanges) {
    const newRemaining = [];
    for (const seg of remaining) {
      if (seg.end < existing.start || seg.start > existing.end) {
        newRemaining.push(seg);
        continue;
      }
      if (seg.start < existing.start) {
        newRemaining.push({ start: seg.start, end: addDays(existing.start, -1) });
      }
      if (seg.end > existing.end) {
        newRemaining.push({ start: addDays(existing.end, 1), end: seg.end });
      }
    }
    remaining = newRemaining;
  }

  return remaining;
};

/**
 * Classify a review relative to existing profile coverage
 */
const classifyReview = (review, coverage, gaps) => {
  const daysBetween = (a, b) => Math.round((new Date(b) - new Date(a)) / 86400000);

  const result = {
    status: 'safe',
    fillsGap: false,
    extendsRange: false,
    overlaps: [],
    nonOverlappingRanges: [],
    gapsFilled: [],
    fullyReplaces: null,
  };

  if (!coverage || !coverage.reviewRanges) return result;

  // Already in profile (match by source path or review id)
  const mergedRange = coverage.reviewRanges.find(r => r.source === review.path || r.source === String(review.id));
  if (mergedRange) {
    // Check if review was updated after it was merged
    if (mergedRange.mergedAt && review.meta?.updatedAt) {
      const mergedTime = new Date(mergedRange.mergedAt);
      const updatedTime = new Date(review.meta.updatedAt);
      if (updatedTime > mergedTime) {
        return { ...result, status: 'needs_update', mergedRange };
      }
    }
    return { ...result, status: 'already_merged' };
  }

  const dr = review.data?.dateRange;
  if (!dr) return result;

  // Check if fills a gap
  if (gaps) {
    result.gapsFilled = gaps.filter(g => dr.start <= g.end && dr.end >= g.start);
    result.fillsGap = result.gapsFilled.length > 0;
  }

  // Check if extends range
  result.extendsRange = dr.end > coverage.end || dr.start < coverage.start;

  // Check overlaps with merged reviews
  result.overlaps = coverage.reviewRanges
    .filter(r => dr.start <= r.end && dr.end >= r.start)
    .map(r => {
      const overlapStart = dr.start > r.start ? dr.start : r.start;
      const overlapEnd = dr.end < r.end ? dr.end : r.end;
      return {
        source: r.source,
        start: r.start,
        end: r.end,
        overlapDays: daysBetween(overlapStart, overlapEnd) + 1,
      };
    });

  if (result.overlaps.length > 0) {
    result.nonOverlappingRanges = getNonOverlappingRanges(dr, coverage.reviewRanges);

    const totalDays = daysBetween(dr.start, dr.end) + 1;
    const avgTradesPerDay = (review.data.stats?.totalTrades || 0) / totalDays;
    result.overlaps = result.overlaps.map(o => ({
      ...o,
      estimatedOverlapTrades: Math.round(avgTradesPerDay * o.overlapDays),
    }));
    result.totalEstimatedOverlap = result.overlaps.reduce(
      (sum, o) => sum + o.estimatedOverlapTrades, 0
    );
  }

  // Detect if this review fully replaces an existing one (same date range)
  if (result.overlaps.length === 1 && result.nonOverlappingRanges.length === 0) {
    const overlap = result.overlaps[0];
    if (overlap.start === dr.start && overlap.end === dr.end) {
      result.fullyReplaces = overlap.source;
    }
  }

  if (result.overlaps.length > 0) {
    result.status = 'caution';
  } else if (result.fillsGap || result.extendsRange) {
    result.status = 'recommended';
  }

  return result;
};

/**
 * Build interleaved timeline of reviews and gaps, sorted by date
 */
function buildTimelineItems(reviews, gaps) {
  const items = [];

  reviews.forEach(r => {
    items.push({
      ...r,
      type: 'review',
      sortDate: r.data?.dateRange?.start || r.path || '',
    });
  });

  (gaps || []).forEach(g => {
    items.push({
      ...g,
      type: 'gap',
      sortDate: g.start || '',
    });
  });

  items.sort((a, b) => (a.sortDate || '').localeCompare(b.sortDate || ''));
  return items;
}

// ==========================================
// Component
// ==========================================

function ProfileUpdateDialog({ onClose, onOpenAiReview }) {
  // Mode
  const [mode, setMode] = useState('rebuild'); // 'rebuild' | 'incremental'

  // Full Rebuild state
  const [profileRange, setProfileRange] = useState('month');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [isScanning, setIsScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [rebuildSelectedPaths, setRebuildSelectedPaths] = useState(new Set());

  // Incremental Update state
  const [existingProfile, setExistingProfile] = useState(null);
  const [availableReviews, setAvailableReviews] = useState([]);
  const [incrementalSelectedPaths, setIncrementalSelectedPaths] = useState(new Set());
  const [incrementalGaps, setIncrementalGaps] = useState([]);
  const [isLoadingIncremental, setIsLoadingIncremental] = useState(false);

  // Shared state
  const [isProcessing, setIsProcessing] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [viewingReviewId, setViewingReviewId] = useState(null);

  // Get date range from profileRange selection
  const getDateRangeForScan = useCallback(() => {
    const now = getETNow();
    const endDate = getETDateString();
    let startDate;

    switch (profileRange) {
      case 'month': {
        // "30 Days" — rolling 30-day window. Earlier this read first-day-of-current-month,
        // which silently returned 0 reviews on day 1 of a new month (e.g. May 1 EDT after
        // the day rolls over) since every prior review fell outside the window.
        const d = new Date(now);
        d.setDate(d.getDate() - 30);
        startDate = d.toLocaleDateString('en-CA');
        break;
      }
      case '3months': {
        const d = new Date(now);
        d.setMonth(d.getMonth() - 3);
        startDate = d.toLocaleDateString('en-CA');
        break;
      }
      case '6months': {
        const d = new Date(now);
        d.setMonth(d.getMonth() - 6);
        startDate = d.toLocaleDateString('en-CA');
        break;
      }
      case '1year': {
        const d = new Date(now);
        d.setFullYear(d.getFullYear() - 1);
        startDate = d.toLocaleDateString('en-CA');
        break;
      }
      case 'all':
        startDate = '2020-01-01';
        break;
      case 'custom':
        startDate = customStartDate || '2020-01-01';
        return { startDate, endDate: customEndDate || endDate };
      default:
        startDate = '2020-01-01';
    }

    return { startDate, endDate };
  }, [profileRange, customStartDate, customEndDate]);

  // Load incremental data when switching to incremental mode
  useEffect(() => {
    if (mode === 'incremental') {
      loadIncrementalData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  // Detect coverage gaps using API data
  const detectGapsFromApi = async (reviewRanges, startDate, endDate) => {
    try {
      const [enrichedResp, simResp] = await Promise.all([
        apiGetEnriched(startDate, endDate).catch(() => null),
        apiGetSimRange(startDate, endDate).catch(() => null),
      ]);

      const dateMap = new Map();

      if (Array.isArray(enrichedResp)) {
        enrichedResp.forEach(item => {
          const date = item.tradeDate;
          const data = typeof item.data === 'string' ? JSON.parse(item.data) : item.data;
          const count = data?.enrichedPairs?.length || 1;
          if (dateMap.has(date)) {
            dateMap.get(date).tradeCount += count;
          } else {
            dateMap.set(date, { date, tradeCount: count, simCount: 0, simEnteredCount: 0 });
          }
        });
      }

      if (Array.isArray(simResp)) {
        simResp.forEach(item => {
          const date = item.simDate;
          const data = typeof item.data === 'string' ? JSON.parse(item.data) : item.data;
          const trades = Array.isArray(data?.trades) ? data.trades : [];
          const count = trades.length || 1;
          const entered = trades.filter(t => t.exitReason && t.exitReason !== 'no_entry' && t.exitReason !== 'no_data').length;
          if (dateMap.has(date)) {
            dateMap.get(date).simCount += count;
            dateMap.get(date).simEnteredCount += entered;
          } else {
            dateMap.set(date, { date, tradeCount: 0, simCount: count, simEnteredCount: entered });
          }
        });
      }

      const allDates = [...dateMap.values()].sort((a, b) => a.date.localeCompare(b.date));
      const uncovered = allDates.filter(item =>
        !reviewRanges.some(r => item.date >= r.start && item.date <= r.end)
      );

      if (uncovered.length === 0) return [];
      const gaps = [];
      let current = { start: uncovered[0].date, end: uncovered[0].date, tradeCount: uncovered[0].tradeCount, simCount: uncovered[0].simCount, simEnteredCount: uncovered[0].simEnteredCount || 0, dateCount: 1 };

      for (let i = 1; i < uncovered.length; i++) {
        const diffDays = Math.round((new Date(uncovered[i].date) - new Date(current.end)) / 86400000);
        if (diffDays <= 3) {
          current.end = uncovered[i].date;
          current.tradeCount += uncovered[i].tradeCount;
          current.simCount += uncovered[i].simCount;
          current.simEnteredCount += uncovered[i].simEnteredCount || 0;
          current.dateCount++;
        } else {
          gaps.push(current);
          current = { start: uncovered[i].date, end: uncovered[i].date, tradeCount: uncovered[i].tradeCount, simCount: uncovered[i].simCount, simEnteredCount: uncovered[i].simEnteredCount || 0, dateCount: 1 };
        }
      }
      gaps.push(current);
      return gaps;
    } catch {
      return [];
    }
  };

  const loadIncrementalData = async () => {
    setIsLoadingIncremental(true);
    setError(null);

    try {
      // 1. Load existing profile from API
      let profile = null;
      try {
        const profileResp = await apiGetProfile();
        if (profileResp?.profileData) {
          profile = typeof profileResp.profileData === 'string' ? JSON.parse(profileResp.profileData) : profileResp.profileData;
        }
        const cov = profileResp?.coverage && profileResp.coverage !== 'null'
          ? (typeof profileResp.coverage === 'string' ? JSON.parse(profileResp.coverage) : profileResp.coverage)
          : null;
        if (cov) {
          if (!profile) profile = {};
          profile.coverage = cov;
        }
      } catch { /* no profile yet */ }
      setExistingProfile(profile);

      // 2. Load all reviews from API
      const reviewsResp = await apiListReviews();
      const reviewList = Array.isArray(reviewsResp) ? reviewsResp : [];

      // Load full data for each review
      const reviewsWithData = [];
      for (const meta of reviewList) {
        try {
          const fullReview = await apiGetReview(meta.id);
          if (fullReview) {
            const extractedProfile = fullReview.extractedProfile
              ? (typeof fullReview.extractedProfile === 'string' ? JSON.parse(fullReview.extractedProfile) : fullReview.extractedProfile)
              : null;
            const anthropicResult = fullReview.anthropicResult
              ? (typeof fullReview.anthropicResult === 'string' ? JSON.parse(fullReview.anthropicResult) : fullReview.anthropicResult)
              : null;
            const openaiResult = fullReview.openaiResult
              ? (typeof fullReview.openaiResult === 'string' ? JSON.parse(fullReview.openaiResult) : fullReview.openaiResult)
              : null;

            reviewsWithData.push({
              id: fullReview.id,
              path: String(fullReview.id),
              meta: {
                date: fullReview.reviewDate,
                timeRange: fullReview.timeRange,
                createdAt: fullReview.createdAt,
                updatedAt: fullReview.updatedAt,
              },
              data: {
                reviewDate: fullReview.reviewDate,
                timeRange: fullReview.timeRange,
                dateRange: {
                  start: fullReview.dateStart,
                  end: fullReview.dateEnd,
                },
                stats: extractedProfile?.stats || { totalTrades: 0 },
                userProfile: extractedProfile?.userProfile || null,
                fullReview: { anthropic: anthropicResult, openai: openaiResult },
              },
            });
          }
        } catch (err) {
          console.log(`Skipping review ${meta.id}:`, err.message);
        }
      }

      // 3. Detect gaps
      const coverage = profile?.coverage || null;
      let gaps = [];
      if (coverage?.reviewRanges?.length > 0) {
        const earliestDate = coverage.start || '2020-01-01';
        const today = getETDateString();
        gaps = await detectGapsFromApi(coverage.reviewRanges, earliestDate, today);
      }
      setIncrementalGaps(gaps);

      // 4. Classify and set available reviews
      const classified = reviewsWithData.map(review => ({
        ...review,
        classification: classifyReview(review, coverage, gaps),
      }));

      setAvailableReviews(classified);

      // 5. Default select recommended reviews
      const defaultSelected = new Set();
      classified.forEach(r => {
        if (r.classification.status === 'recommended') {
          defaultSelected.add(r.path);
        }
      });
      setIncrementalSelectedPaths(defaultSelected);
    } catch (err) {
      console.error('Error loading incremental data:', err);
      setError(`Failed to load profile data: ${err.message}`);
    } finally {
      setIsLoadingIncremental(false);
    }
  };

  const handleScan = async () => {
    setIsScanning(true);
    setError(null);
    setScanResult(null);
    setResult(null);

    try {
      const { startDate, endDate } = getDateRangeForScan();

      // Load reviews from API in date range
      const reviewsResp = await apiListReviews(startDate, endDate);
      const reviewList = Array.isArray(reviewsResp) ? reviewsResp : [];

      // Load full data for each
      const reviewsWithData = [];
      for (const meta of reviewList) {
        try {
          const fullReview = await apiGetReview(meta.id);
          if (fullReview) {
            const extractedProfile = fullReview.extractedProfile
              ? (typeof fullReview.extractedProfile === 'string' ? JSON.parse(fullReview.extractedProfile) : fullReview.extractedProfile)
              : null;

            const reviewData = {
              reviewDate: fullReview.reviewDate,
              timeRange: fullReview.timeRange,
              dateRange: { start: fullReview.dateStart, end: fullReview.dateEnd },
              stats: extractedProfile?.stats || { totalTrades: 0 },
              userProfile: extractedProfile?.userProfile || null,
            };

            if (reviewData.dateRange.start && reviewData.dateRange.end) {
              reviewsWithData.push({
                id: fullReview.id,
                path: String(fullReview.id),
                meta: { date: fullReview.reviewDate, timeRange: fullReview.timeRange, createdAt: fullReview.createdAt, updatedAt: fullReview.updatedAt },
                data: reviewData,
              });
            }
          }
        } catch (err) {
          console.log(`Skipping review ${meta.id}:`, err.message);
        }
      }

      // Simple dedup: remove reviews fully contained by another
      const selected = [];
      const duplicates = [];
      const sorted = [...reviewsWithData].sort((a, b) =>
        (a.data.dateRange?.start || '').localeCompare(b.data.dateRange?.start || '')
      );

      for (const review of sorted) {
        const containedBy = sorted.find(other =>
          other !== review &&
          other.data.dateRange?.start <= review.data.dateRange?.start &&
          other.data.dateRange?.end >= review.data.dateRange?.end &&
          (other.data.stats?.totalTrades || 0) >= (review.data.stats?.totalTrades || 0)
        );

        if (containedBy) {
          duplicates.push({
            review,
            containedBy: containedBy.path,
            containedByRange: `${containedBy.data.dateRange.start} ~ ${containedBy.data.dateRange.end}`,
          });
        } else {
          selected.push(review);
        }
      }

      // Detect gaps
      const reviewRanges = selected.map(r => ({
        source: r.path, start: r.data.dateRange.start, end: r.data.dateRange.end
      }));
      const gaps = await detectGapsFromApi(reviewRanges, startDate, endDate);

      setScanResult({ allReviews: reviewsWithData, selected, duplicates, gaps });

      // Default select all non-duplicate reviews
      const paths = new Set(selected.map(r => r.path));
      setRebuildSelectedPaths(paths);
    } catch (err) {
      console.error('Error scanning reviews:', err);
      setError(`Scan failed: ${err.message}`);
    } finally {
      setIsScanning(false);
    }
  };

  const handleRebuild = async () => {
    if (!scanResult) return;

    setIsProcessing(true);
    setError(null);

    try {
      const selectedReviews = scanResult.selected.filter(r => rebuildSelectedPaths.has(r.path));
      if (selectedReviews.length === 0) {
        setError('No reviews selected for rebuild.');
        setIsProcessing(false);
        return;
      }

      // Sort by date ascending
      const sorted = [...selectedReviews].sort(
        (a, b) => (a.data.dateRange?.start || '').localeCompare(b.data.dateRange?.start || '')
      );

      // Backend rebuilds profile + coverage atomically in one call (mode=rebuild).
      const reviewIds = sorted.map(r => r.id).filter(Boolean);
      if (reviewIds.length === 0) {
        setError('No valid review IDs to merge.');
        setIsProcessing(false);
        return;
      }
      const mergeResp = await apiMergeReviewsIntoProfile(reviewIds, 'rebuild');

      // Fetch fresh profile + coverage from backend
      const profileResp = await apiGetProfile();
      const profileData = profileResp?.profileData
        ? (typeof profileResp.profileData === 'string' ? JSON.parse(profileResp.profileData) : profileResp.profileData)
        : null;
      const coverage = mergeResp?.coverage
        ? (typeof mergeResp.coverage === 'string' ? JSON.parse(mergeResp.coverage) : mergeResp.coverage)
        : null;

      setResult({
        totalPatterns: (profileData?.userProfile?.successfulPatterns?.length || 0) +
                       (profileData?.userProfile?.avoidPatterns?.length || 0),
        quickStats: profileData?.quickStats || null,
        coverage,
      });
    } catch (err) {
      console.error('Error rebuilding profile:', err);
      setError(`Rebuild failed: ${err.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleMergeSelected = async () => {
    setIsProcessing(true);
    setError(null);

    try {
      const selectedReviews = availableReviews.filter(r => incrementalSelectedPaths.has(r.path));
      if (selectedReviews.length === 0) {
        setError('No reviews selected for merge.');
        setIsProcessing(false);
        return;
      }

      // Sort by date ascending
      const sorted = [...selectedReviews].sort(
        (a, b) => (a.data.dateRange?.start || '').localeCompare(b.data.dateRange?.start || '')
      );

      // Backend appends to profile + coverage atomically (mode=append, default).
      const reviewIds = sorted.map(r => r.id).filter(Boolean);
      if (reviewIds.length === 0) {
        setError('No valid review IDs to merge.');
        setIsProcessing(false);
        return;
      }
      const mergeResp = await apiMergeReviewsIntoProfile(reviewIds, 'append');

      const profileResp = await apiGetProfile();
      const profileData = profileResp?.profileData
        ? (typeof profileResp.profileData === 'string' ? JSON.parse(profileResp.profileData) : profileResp.profileData)
        : null;
      const coverage = mergeResp?.coverage
        ? (typeof mergeResp.coverage === 'string' ? JSON.parse(mergeResp.coverage) : mergeResp.coverage)
        : null;

      setResult({
        mergedCount: sorted.length,
        totalPatterns: (profileData?.userProfile?.successfulPatterns?.length || 0) +
                       (profileData?.userProfile?.avoidPatterns?.length || 0),
        quickStats: profileData?.quickStats || null,
        coverage,
      });
    } catch (err) {
      console.error('Error merging reviews:', err);
      setError(`Merge failed: ${err.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReplaceReview = async (newReview, oldReviewSource) => {
    setIsProcessing(true);
    setError(null);

    try {
      // 1. Collect all currently merged reviews, replacing old with new
      const coverage = existingProfile?.coverage;
      const mergedRanges = coverage?.reviewRanges || [];

      // Get all review IDs that are currently merged, excluding the one being replaced
      const keepIds = mergedRanges
        .map(r => r.source)
        .filter(s => s !== oldReviewSource);

      // Find the full review data for each kept review from availableReviews list
      const allReviews = availableReviews;
      const reviewsToMerge = [];

      for (const id of keepIds) {
        const found = allReviews.find(r => String(r.id) === id || r.path === id);
        if (found) {
          reviewsToMerge.push(found);
        }
      }

      // Add the new review
      reviewsToMerge.push(newReview);

      // Sort by date ascending
      const sorted = [...reviewsToMerge].sort(
        (a, b) => (a.data.dateRange?.start || '').localeCompare(b.data.dateRange?.start || '')
      );

      // 2. Backend rebuilds profile + coverage with "replace" mode: clear then re-merge,
      //    preserving original mergedAt for any review source that existed in old coverage.
      const reviewIds = sorted.map(r => r.id).filter(Boolean);
      if (reviewIds.length === 0) {
        setError('No valid review IDs to merge.');
        setIsProcessing(false);
        return;
      }
      const mergeResp = await apiMergeReviewsIntoProfile(reviewIds, 'replace');

      // 3. Show result
      const profileResp = await apiGetProfile();
      const profileData = profileResp?.profileData
        ? (typeof profileResp.profileData === 'string' ? JSON.parse(profileResp.profileData) : profileResp.profileData)
        : null;
      const newCoverage = mergeResp?.coverage
        ? (typeof mergeResp.coverage === 'string' ? JSON.parse(mergeResp.coverage) : mergeResp.coverage)
        : null;

      setResult({
        mergedCount: sorted.length,
        totalPatterns: (profileData?.userProfile?.successfulPatterns?.length || 0) +
                       (profileData?.userProfile?.avoidPatterns?.length || 0),
        quickStats: profileData?.quickStats || null,
        coverage: newCoverage,
      });

      // Refresh incremental data
      loadIncrementalData();
    } catch (err) {
      console.error('Error replacing review:', err);
      setError(`Replace failed: ${err.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDeleteReview = async (reviewId) => {
    setDeleteConfirm(null);

    try {
      await apiDeleteReview(reviewId);

      // Refresh current list
      if (mode === 'rebuild' && scanResult) {
        handleScan();
      } else if (mode === 'incremental') {
        loadIncrementalData();
      }
    } catch (err) {
      console.error('Error deleting review:', err);
      setError(`Delete failed: ${err.message}`);
    }
  };

  const handleOpenAiReviewForGap = (gap) => {
    if (onOpenAiReview) {
      onOpenAiReview(gap.start, gap.end);
    }
  };

  const handleOpenAiReviewForOverlap = (review) => {
    const coverage = existingProfile?.coverage;
    if (!coverage || !review.data.dateRange) return;

    const ranges = getNonOverlappingRanges(review.data.dateRange, coverage.reviewRanges);
    if (ranges.length === 0) {
      setError('This review is fully contained by existing coverage. Consider deleting it.');
      return;
    }

    if (onOpenAiReview) {
      onOpenAiReview(ranges[0].start, ranges[0].end);
    }
  };

  const toggleRebuildSelection = (path) => {
    setRebuildSelectedPaths(prev => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  };

  const toggleIncrementalSelection = (path) => {
    setIncrementalSelectedPaths(prev => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  };

  const formatDateRange = (start, end) => {
    if (!start || !end) return 'Unknown';
    if (start === end) return start;
    return `${start} ~ ${end}`;
  };

  return (
    <>
    <div className="profile-update-overlay" onClick={onClose}>
      <div className="profile-update-dialog" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="profile-update-header">
          <h2>AI Profile</h2>
          <button className="close-btn" onClick={onClose}>x</button>
        </div>

        {/* Mode Selector */}
        <div className="profile-update-toolbar">
          <div className="mode-selector">
            <button
              className={mode === 'rebuild' ? 'active' : ''}
              onClick={() => { setMode('rebuild'); setResult(null); setError(null); }}
            >
              Full Rebuild
            </button>
            <button
              className={mode === 'incremental' ? 'active' : ''}
              onClick={() => { setMode('incremental'); setResult(null); setError(null); }}
            >
              Incremental Update
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="profile-update-content">
          {error && (
            <div className="profile-error-message">{error}</div>
          )}

          {/* ====== Full Rebuild Mode ====== */}
          {mode === 'rebuild' && (
            <>
              {/* Time Range Selector */}
              <div className="profile-range-section">
                <label>Time Range:</label>
                <div className="range-buttons">
                  {[
                    { key: 'month', label: '30 Days' },
                    { key: '3months', label: '3 Months' },
                    { key: '6months', label: '6 Months' },
                    { key: '1year', label: '1 Year' },
                    { key: 'all', label: 'All' },
                    { key: 'custom', label: 'Custom' },
                  ].map(opt => (
                    <button
                      key={opt.key}
                      className={profileRange === opt.key ? 'active' : ''}
                      onClick={() => setProfileRange(opt.key)}
                    >
                      {opt.label}
                    </button>
                  ))}

                  {profileRange === 'custom' && (
                    <>
                      <input type="date" className="custom-date-input" value={customStartDate} onChange={e => setCustomStartDate(e.target.value)} />
                      <span>~</span>
                      <input type="date" className="custom-date-input" value={customEndDate} onChange={e => setCustomEndDate(e.target.value)} />
                    </>
                  )}

                  <button
                    className="btn-primary"
                    onClick={handleScan}
                    disabled={isScanning || (profileRange === 'custom' && (!customStartDate || !customEndDate))}
                  >
                    {isScanning ? 'Scanning...' : 'Scan Reviews'}
                  </button>
                </div>
              </div>

              {/* Scan Results */}
              {scanResult && (
                <div className="scan-results">
                  <h4>
                    📅 Coverage Timeline ({scanResult.selected.length} reviews
                    {scanResult.gaps.length > 0 && `, ${scanResult.gaps.length} gaps`})
                  </h4>

                  <div className="review-file-list">
                    {buildTimelineItems(scanResult.selected, scanResult.gaps).map((item, idx) => {
                      if (item.type === 'gap') {
                        return (
                          <div key={`gap-${idx}`} className="review-file-item gap-item">
                            <span className="gap-icon">!</span>
                            <div className="review-file-info">
                              <span className="review-date-range">
                                {formatDateRange(item.start, item.end)}
                              </span>
                              <span className="gap-label">
                                GAP - {item.tradeCount > 0 && item.simCount > 0
                                  ? `${item.tradeCount} trades + ${item.simEnteredCount}/${item.simCount} simulated not reviewed`
                                  : item.tradeCount > 0
                                    ? `${item.tradeCount} trades not reviewed`
                                    : `${item.simEnteredCount}/${item.simCount} simulated trades not reviewed`
                                }
                              </span>
                            </div>
                            <button
                              className="gap-action-btn"
                              onClick={() => handleOpenAiReviewForGap(item)}
                            >
                              Open AI Review
                            </button>
                          </div>
                        );
                      }

                      const review = item;
                      const isSelected = rebuildSelectedPaths.has(review.path);
                      return (
                        <div key={review.path} className={`review-file-item ${isSelected ? 'selected' : 'deselected'}`}>
                          <label className="review-checkbox">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleRebuildSelection(review.path)}
                            />
                          </label>
                          <div className="review-file-info">
                            <span className="review-date-range">
                              {formatDateRange(review.data.dateRange?.start, review.data.dateRange?.end)}
                            </span>
                            <span className="review-file-name">{timeRangeLabel(review.meta?.timeRange)} Review #{review.id}</span>
                            <span className="review-pairs">
                              ({review.data.stats?.entered != null
                                ? `${review.data.stats.entered}/${review.data.stats.totalTrades}`
                                : review.data.stats?.totalTrades || 0} trades)
                            </span>
                          </div>
                          <button
                            className="review-view-btn"
                            onClick={(e) => { e.stopPropagation(); setViewingReviewId(review.id); }}
                            title="View review details"
                          >
                            View
                          </button>
                          <button
                            className="review-delete-btn"
                            onClick={() => setDeleteConfirm({ path: review.id, name: `review #${review.id}` })}
                            title="Delete review"
                          >
                            🗑️
                          </button>
                        </div>
                      );
                    })}
                  </div>

                  {/* Duplicates */}
                  {scanResult.duplicates.length > 0 && (
                    <div className="duplicates-section">
                      <h5>Duplicates (auto-removed)</h5>
                      {scanResult.duplicates.map((dup, idx) => (
                        <div key={idx} className="review-file-item review-file-dup">
                          <span className="dup-icon">x</span>
                          <div className="review-file-info">
                            <span className="review-file-name">review #{dup.review.id}</span>
                            <span className="dup-reason">
                              contained in: #{dup.containedBy} ({dup.containedByRange})
                            </span>
                          </div>
                          <button
                            className="review-delete-btn"
                            onClick={() => setDeleteConfirm({ path: dup.review.id, name: `review #${dup.review.id}` })}
                            title="Delete review"
                          >
                            🗑️
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Rebuild Result */}
              {result && (
                <div className="rebuild-result">
                  <h4>{result.mergedCount ? 'Merge Complete' : 'Rebuild Complete'}</h4>
                  <div className="rebuild-stats">
                    {result.mergedCount && <span>Merged: {result.mergedCount} reviews</span>}
                    {result.quickStats && (
                      <>
                        <span>Win Rate: {result.quickStats.winRate}%</span>
                        <span>Total Trades: {result.quickStats.totalTrades?.toLocaleString()}</span>
                      </>
                    )}
                    <span>Patterns: {result.totalPatterns}</span>
                    {result.coverage && (
                      <span>Coverage: {formatDateRange(result.coverage.start, result.coverage.end)}</span>
                    )}
                  </div>
                </div>
              )}
            </>
          )}

          {/* ====== Incremental Update Mode ====== */}
          {mode === 'incremental' && (
            <>
              {isLoadingIncremental && (
                <div className="loading-state">Loading profile data...</div>
              )}

              {!isLoadingIncremental && existingProfile && (
                <>
                  {/* Current Profile Coverage */}
                  <div className="current-coverage-section">
                    <h4>📅 Current Profile Coverage</h4>
                    {existingProfile.quickStats && (
                      <div className="coverage-stats">
                        <span>Win Rate: {existingProfile.quickStats.winRate}%</span>
                        <span>Trades: {existingProfile.userProfile?.pairsAnalyzed != null && existingProfile.userProfile.pairsAnalyzed !== existingProfile.quickStats.totalTrades
                          ? `${existingProfile.userProfile.pairsAnalyzed}/${existingProfile.quickStats.totalTrades}`
                          : existingProfile.quickStats.totalTrades?.toLocaleString()}</span>
                        <span>Patterns: {existingProfile.quickStats.totalPatterns}</span>
                      </div>
                    )}

                    {existingProfile.coverage?.reviewRanges && (
                      <div className="review-file-list">
                        {buildTimelineItems(
                          existingProfile.coverage.reviewRanges.map(r => ({
                              path: r.source,
                              meta: { timeRange: r.timeRange, reviewedAt: r.reviewedAt, mergedAt: r.mergedAt },
                              data: { dateRange: { start: r.start, end: r.end }, stats: { totalTrades: r.pairs, entered: r.entered } },
                          })),
                          incrementalGaps
                        ).map((item, idx) => {
                          if (item.type === 'gap') {
                            return (
                              <div key={`gap-${idx}`} className="review-file-item gap-item">
                                <span className="gap-icon">!</span>
                                <div className="review-file-info">
                                  <span className="review-date-range">
                                    {formatDateRange(item.start, item.end)}
                                  </span>
                                  <span className="gap-label">
                                    GAP - {item.tradeCount > 0 && item.simCount > 0
                                      ? `${item.tradeCount} trades + ${item.simEnteredCount}/${item.simCount} simulated not reviewed`
                                      : item.tradeCount > 0
                                        ? `${item.tradeCount} trades not reviewed`
                                        : `${item.simEnteredCount}/${item.simCount} simulated trades not reviewed`
                                    }
                                  </span>
                                </div>
                                <button
                                  className="gap-action-btn"
                                  onClick={() => handleOpenAiReviewForGap(item)}
                                >
                                  Open AI Review
                                </button>
                              </div>
                            );
                          }

                          return (
                            <div key={`merged-${idx}`} className="review-file-item merged-item">
                              <span className="merged-icon">&#10003;</span>
                              <div className="review-file-info">
                                <span className="review-date-range">
                                  {formatDateRange(item.data.dateRange?.start, item.data.dateRange?.end)}
                                </span>
                                <span className="review-file-name">{timeRangeLabel(item.meta?.timeRange)} Review #{item.path}</span>
                                <span className="review-pairs">
                                  ({item.data.stats?.entered != null
                                    ? `${item.data.stats.entered}/${item.data.stats.totalTrades}`
                                    : item.data.stats?.totalTrades || 0} trades)
                                </span>
                                {item.meta?.reviewedAt && (
                                  <span className="merged-at-label">reviewed: {formatTimestamp(item.meta.reviewedAt)}</span>
                                )}
                                {item.meta?.mergedAt && (
                                  <span className="merged-at-label">merged: {formatTimestamp(item.meta.mergedAt)}</span>
                                )}
                              </div>
                            </div>
                          );
                        })}

                        <div className="coverage-summary">
                          {existingProfile.coverage.reviewRanges?.length || 0} reviews merged
                          {incrementalGaps.length > 0 && `, ${incrementalGaps.length} gaps detected`}
                        </div>
                      </div>
                    )}

                    {!existingProfile.coverage && (
                      <div className="no-coverage-message">
                        No coverage data found. Consider using Full Rebuild first.
                      </div>
                    )}
                  </div>

                  {/* Available Reviews */}
                  <div className="available-reviews-section">
                    <h4>📥 Available Reviews</h4>
                    <div className="review-file-list">
                      {availableReviews
                        .filter(r => r.classification.status !== 'already_merged')
                        .sort((a, b) => {
                          const order = { needs_update: 0, recommended: 1, caution: 2, safe: 3 };
                          return (order[a.classification.status] || 4) - (order[b.classification.status] || 4);
                        })
                        .map((review) => {
                          const cls = review.classification;
                          const isSelected = incrementalSelectedPaths.has(review.path);

                          return (
                            <div key={review.path} className={`review-file-item classify-${cls.status}`}>
                              {cls.status !== 'already_merged' && cls.status !== 'caution' && (
                                <label className="review-checkbox">
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => toggleIncrementalSelection(review.path)}
                                  />
                                </label>
                              )}
                              <div className="review-file-info">
                                <div className="review-file-main">
                                  <span className={`review-status-badge status-${cls.status}`}>
                                    {cls.status === 'needs_update' ? '\u21BB'
                                      : cls.status === 'recommended' ? '>'
                                      : cls.status === 'caution'
                                        ? (cls.nonOverlappingRanges.length > 0 ? '!' : 'x')
                                        : '-'}
                                  </span>
                                  <span className="review-date-range">
                                    {formatDateRange(review.data.dateRange?.start, review.data.dateRange?.end)}
                                  </span>
                                  <span className="review-file-name">{timeRangeLabel(review.meta?.timeRange)} Review #{review.id}</span>
                                  <span className="review-pairs">
                                    ({review.data.stats?.entered != null
                                      ? `${review.data.stats.entered}/${review.data.stats.totalTrades}`
                                      : review.data.stats?.totalTrades || 0} trades)
                                  </span>
                                  {review.meta?.updatedAt && new Date(review.meta.updatedAt) > new Date(review.meta.createdAt) && (
                                    <span className="merged-at-label">updated: {formatTimestamp(review.meta.updatedAt)}</span>
                                  )}
                                </div>

                                {/* Classification details */}
                                <div className="review-classification-details">
                                  {cls.fillsGap && (
                                    <span className="cls-tag fills-gap">
                                      Fills gap: {cls.gapsFilled.map(g => formatDateRange(g.start, g.end)).join(', ')}
                                    </span>
                                  )}
                                  {cls.extendsRange && (
                                    <span className="cls-tag extends-range">Extends coverage</span>
                                  )}
                                  {cls.overlaps.length > 0 && (
                                    <div className="overlap-details">
                                      <span className="overlap-label">
                                        Overlaps: {cls.overlaps.map(o =>
                                          `#${o.source} (${o.overlapDays}d)`
                                        ).join(', ')}
                                      </span>
                                      {cls.nonOverlappingRanges.length > 0 && (
                                        <span className="non-overlap-label">
                                          Non-overlapping: {cls.nonOverlappingRanges.map(r =>
                                            formatDateRange(r.start, r.end)
                                          ).join(', ')}
                                        </span>
                                      )}
                                    </div>
                                  )}
                                </div>

                                {/* Action for needs_update reviews */}
                                {cls.status === 'needs_update' && cls.mergedRange && (
                                  <div className="overlap-actions">
                                    <button
                                      className="overlap-action-btn update-btn"
                                      onClick={() => handleReplaceReview(review, cls.mergedRange.source)}
                                      disabled={isProcessing}
                                    >
                                      {isProcessing ? 'Updating...' : `Update Review #${review.id}`}
                                    </button>
                                  </div>
                                )}

                                {/* Action buttons for overlapping reviews */}
                                {cls.status === 'caution' && (
                                  <div className="overlap-actions">
                                    {cls.fullyReplaces ? (
                                      <button
                                        className="overlap-action-btn replace-btn"
                                        onClick={() => handleReplaceReview(review, cls.fullyReplaces)}
                                        disabled={isProcessing}
                                      >
                                        {isProcessing ? 'Replacing...' : `Replace #${cls.fullyReplaces} with #${review.id}`}
                                      </button>
                                    ) : cls.nonOverlappingRanges.length > 0 ? (
                                      <button
                                        className="overlap-action-btn"
                                        onClick={() => handleOpenAiReviewForOverlap(review)}
                                      >
                                        Open AI Review for {formatDateRange(cls.nonOverlappingRanges[0].start, cls.nonOverlappingRanges[0].end)}
                                      </button>
                                    ) : (
                                      <span className="fully-covered-hint">
                                        Fully covered by existing profile. Delete or use Full Rebuild.
                                      </span>
                                    )}
                                  </div>
                                )}
                              </div>
                              <button
                                className="review-delete-btn"
                                onClick={() => setDeleteConfirm({ path: review.id, name: `review #${review.id}` })}
                                title="Delete review"
                              >
                                🗑️
                              </button>
                            </div>
                          );
                        })}

                      {availableReviews.filter(r => r.classification.status !== 'already_merged').length === 0 && (
                        <div className="no-reviews-message">
                          No new reviews available. All reviews are already merged into the profile.
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}

              {!isLoadingIncremental && !existingProfile && (
                <div className="no-profile-message">
                  No existing profile found. Use Full Rebuild to create one.
                </div>
              )}

              {/* Merge Result */}
              {result && (
                <div className="rebuild-result">
                  <h4>{result.mergedCount ? 'Merge Complete' : 'Rebuild Complete'}</h4>
                  <div className="rebuild-stats">
                    {result.mergedCount && <span>Merged: {result.mergedCount} reviews</span>}
                    {result.quickStats && (
                      <>
                        <span>Win Rate: {result.quickStats.winRate}%</span>
                        <span>Total Trades: {result.quickStats.totalTrades?.toLocaleString()}</span>
                      </>
                    )}
                    <span>Patterns: {result.totalPatterns}</span>
                    {result.coverage && (
                      <span>Coverage: {formatDateRange(result.coverage.start, result.coverage.end)}</span>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="profile-update-footer">
          {mode === 'rebuild' && scanResult && (
            <button
              className="btn-primary"
              onClick={handleRebuild}
              disabled={isProcessing || rebuildSelectedPaths.size === 0}
            >
              {isProcessing ? 'Rebuilding...' : `Rebuild Profile (${rebuildSelectedPaths.size} reviews)`}
            </button>
          )}
          {mode === 'incremental' && !isLoadingIncremental && (
            <button
              className="btn-primary"
              onClick={handleMergeSelected}
              disabled={isProcessing || incrementalSelectedPaths.size === 0}
            >
              {isProcessing ? 'Merging...' : `Merge Selected (${incrementalSelectedPaths.size})`}
            </button>
          )}
        </div>

        {/* Delete Confirmation */}
        {deleteConfirm && (
          <div className="confirm-delete-overlay" onClick={() => setDeleteConfirm(null)}>
            <div className="confirm-delete-dialog" onClick={(e) => e.stopPropagation()}>
              <p>Delete <strong>{deleteConfirm.name}</strong>?</p>
              <p className="confirm-delete-warning">This cannot be undone.</p>
              <div className="confirm-delete-buttons">
                <button
                  className="btn-danger"
                  onClick={() => handleDeleteReview(deleteConfirm.path)}
                >
                  Delete
                </button>
                <button
                  className="btn-secondary"
                  onClick={() => setDeleteConfirm(null)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
    {viewingReviewId && (
      <ReviewDetailDialog
        reviewId={viewingReviewId}
        onClose={() => setViewingReviewId(null)}
      />
    )}
    </>
  );
}

export default ProfileUpdateDialog;
